import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * FavoriteBusinessButton
 *
 * Lets a signed-in customer favorite a business. Favorites are the same
 * physical record as follows (business_followers), so favoriting also
 * subscribes them to that merchant's push firehose. Favorites are counted
 * as a ranking factor in the directory algorithm.
 *
 * Shows a heart icon. Filled + red when favorited; outline otherwise.
 * Anonymous visitors see a "Sign in to favorite" link.
 *
 * Props:
 *   businessId: string
 *   compact?: boolean — icon-only variant for use in tight rows (Directory)
 *   initialCount?: number — hydrated count so we can render without a fetch
 */
export default function FavoriteBusinessButton({ businessId, compact = false, initialCount }) {
  const { user } = useAuth();
  const [following, setFollowing] = useState(false);
  const [count, setCount] = useState(initialCount ?? 0);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/businesses/${businessId}/follow`);
        if (cancelled) return;
        setFollowing(!!data?.following);
        setCount(data?.follower_count ?? 0);
      } catch (e) {
        // non-fatal — leave defaults
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  const toggle = async () => {
    if (!user || user.role !== "customer") {
      toast.error("Sign in as a customer to favorite this business");
      return;
    }
    setWorking(true);
    // optimistic UI
    const nextFollowing = !following;
    setFollowing(nextFollowing);
    setCount((c) => Math.max(0, c + (nextFollowing ? 1 : -1)));
    try {
      const { data } = nextFollowing
        ? await api.post(`/businesses/${businessId}/follow`)
        : await api.delete(`/businesses/${businessId}/follow`);
      setCount(data?.follower_count ?? 0);
      setFollowing(!!data?.following);
      toast.success(nextFollowing ? "Added to your favorites" : "Removed from favorites");
    } catch (err) {
      // rollback
      setFollowing(!nextFollowing);
      setCount((c) => Math.max(0, c + (nextFollowing ? -1 : 1)));
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setWorking(false);
    }
  };

  if (!user) {
    return (
      <Link
        to="/login"
        className="inline-flex items-center gap-1.5 rounded-[35px] px-3 py-1.5 border border-border bg-white text-gray-700 hover:bg-gray-50 text-sm"
        data-testid="favorite-signin-cta"
      >
        <Heart size={14} className="text-gray-400" />
        {count > 0 ? count.toLocaleString() : "Favorite"}
      </Link>
    );
  }

  // Businesses/admins see the count but can't favorite themselves
  if (user.role !== "customer") {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 border border-border bg-white text-gray-600 text-xs font-mono"
        data-testid="favorite-count-display"
        title="Total customer favorites"
      >
        <Heart size={12} className="text-rose-400" />
        {count.toLocaleString()}
      </span>
    );
  }

  const btnClasses = following
    ? "rounded-[35px] bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100"
    : "rounded-[35px] bg-white text-gray-700 border border-border hover:bg-gray-50";

  if (compact) {
    return (
      <button
        type="button"
        onClick={toggle}
        disabled={working || loading}
        className={`inline-flex items-center justify-center h-8 w-8 shrink-0 transition-colors ${btnClasses}`}
        aria-label={following ? "Unfavorite" : "Favorite"}
        title={following ? "Favorited — click to remove" : "Add to your favorites"}
        data-testid={`favorite-toggle-${businessId}`}
      >
        {working ? (
          <Loader2 size={13} className="animate-spin" />
        ) : (
          <Heart size={13} fill={following ? "currentColor" : "none"} />
        )}
      </button>
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      onClick={toggle}
      disabled={working || loading}
      className={`${btnClasses} shrink-0`}
      data-testid={`favorite-toggle-${businessId}`}
    >
      {working ? (
        <Loader2 size={14} className="mr-1.5 animate-spin" />
      ) : (
        <Heart
          size={14}
          className="mr-1.5"
          fill={following ? "currentColor" : "none"}
        />
      )}
      {following ? "Favorited" : "Favorite"}
      <span className="ml-1.5 text-xs font-mono opacity-80">
        {count.toLocaleString()}
      </span>
    </Button>
  );
}
